/**
 * PT Cable calculation utilities
 * These calculations follow EN 1992-1-1 and EN 10138 standards
 */

// Standard cover to tendon centerline (mm) - accounts for concrete cover + duct radius
export const PT_COVER_MM = 60

// Minimum allowable eccentricity (m)
export const MIN_ECCENTRICITY_M = 0.05

// Default fallback eccentricity when section height is not available (m)
export const DEFAULT_FALLBACK_ECCENTRICITY_M = 0.20

/**
 * Calculate maximum practical eccentricity for PT cable based on section height.
 * Formula: e_max = h/2 - cover
 *
 * @param sectionHeightMm - Section height in millimeters
 * @param coverMm - Cover to tendon centerline in mm (default: 60mm)
 * @returns Maximum eccentricity in meters
 */
export function calculateMaxEccentricity(sectionHeightMm: number | undefined, coverMm: number = PT_COVER_MM): number {
  if (!sectionHeightMm || sectionHeightMm <= 0) {
    return DEFAULT_FALLBACK_ECCENTRICITY_M
  }

  const heightM = sectionHeightMm / 1000
  const coverM = coverMm / 1000
  const maxEcc = (heightM / 2) - coverM

  return Math.max(MIN_ECCENTRICITY_M, maxEcc)
}

/**
 * Calculate maximum allowed initial stress per EC2 5.10.3(2).
 * Formula: σ_p,max = min(0.80 × f_pk, 0.90 × f_p0.1k)
 *
 * @param fpk - Characteristic tensile strength (MPa)
 * @param fp01k - 0.1% proof stress (MPa)
 * @returns Maximum allowed stress in MPa
 */
export function calculateMaxAllowedStress(fpk: number | undefined, fp01k: number | undefined): number {
  if (!fpk || !fp01k) {
    return 0
  }
  return Math.min(0.80 * fpk, 0.90 * fp01k)
}

/**
 * Calculate initial prestress force from strand data.
 * Formula: P0 = n × A_strand × σ_p
 *
 * @param strandCount - Number of strands
 * @param strandAreaMm2 - Area per strand in mm²
 * @param stressMPa - Applied stress in MPa
 * @returns Prestress force in kN
 */
export function calculatePrestressForce(
  strandCount: number,
  strandAreaMm2: number,
  stressMPa: number
): number {
  // P0 (kN) = n × A (mm²) × σ (MPa) / 1000
  // Note: MPa × mm² = N, so divide by 1000 to get kN
  return (strandCount * strandAreaMm2 * stressMPa) / 1000
}

/**
 * Calculate total cable area.
 *
 * @param strandCount - Number of strands
 * @param strandAreaMm2 - Area per strand in mm²
 * @returns Total cable area in mm²
 */
export function calculateCableArea(strandCount: number, strandAreaMm2: number): number {
  return strandCount * strandAreaMm2
}

/**
 * Calculate stress from force and area.
 *
 * @param forceKN - Force in kN
 * @param areaMm2 - Area in mm²
 * @returns Stress in MPa
 */
export function calculateStress(forceKN: number, areaMm2: number): number {
  if (areaMm2 <= 0) return 0
  // σ (MPa) = F (kN) × 1000 / A (mm²)
  return (forceKN * 1000) / areaMm2
}

/**
 * Validate eccentricity is within allowable range.
 *
 * @param eccentricityM - Eccentricity in meters
 * @param sectionHeightMm - Section height in millimeters
 * @param coverMm - Cover to tendon centerline in mm
 * @returns true if valid, false otherwise
 */
export function isEccentricityValid(
  eccentricityM: number,
  sectionHeightMm: number,
  coverMm: number = PT_COVER_MM
): boolean {
  if (sectionHeightMm <= 0) return false
  const maxEcc = calculateMaxEccentricity(sectionHeightMm, coverMm)
  return eccentricityM >= 0 && eccentricityM <= maxEcc
}

/**
 * Standard strand presets per EN 10138
 */
export const STRAND_PRESETS = [
  { name: '12.5 mm (7-wire)', areaMm2: 93, diameter: 12.5 },
  { name: '12.9 mm (7-wire)', areaMm2: 100, diameter: 12.9 },
  { name: '15.2 mm (7-wire)', areaMm2: 140, diameter: 15.2 },
  { name: '15.7 mm (7-wire)', areaMm2: 150, diameter: 15.7 },
  { name: '18.0 mm (7-wire)', areaMm2: 200, diameter: 18.0 },
] as const
